import { ApplicationConfig, InjectionToken, importProvidersFrom, provideZonelessChangeDetection, provideBrowserGlobalErrorListeners } from '@angular/core';
import { PreloadAllModules, provideRouter, withComponentInputBinding, withInMemoryScrolling, withNavigationErrorHandler, withPreloading, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, withXhr, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthInterceptor } from 'src/app/services/auth.interceptor';
import { DevGateInterceptor } from 'src/app/services/dev-gate.interceptor';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { MessageService } from 'primeng/api';
import { routes } from './app.routes';
import { providePrimeNG } from 'primeng/config';
import { UserStoreService } from 'src/app/services/user.store';
import { ModalDialogComponent } from 'src/app/components/dialogs/modal-dialog/modal-dialog.component';
import { createTeamsConfig } from 'src/app/pages/teams/config/teams.config';
import { PixelModule } from 'ngx-pixel-eventid';
import { environment } from 'src/environments/environment';
import { MarkdownModule, MARKED_EXTENSIONS } from 'ngx-markdown';
import { MarkedExtension } from 'marked';
import { MDTThemePreset } from 'src/assets/theme/MDT-Theme-Preset';

export const WINDOW_TOKEN = new InjectionToken<Window>('WindowToken');

// Forces every markdown-rendered link (model-authored, e.g. "[Song](https://...)")
// to open in a new tab. Citation links are built as raw HTML elsewhere and carry
// their own target/rel attributes since marked passes raw inline HTML through untouched.
function markedTargetBlankExtension(): MarkedExtension {
  return {
    renderer: {
      link({ href, title, tokens }) {
        const text = this.parser.parseInline(tokens);
        const titleAttr = title ? ` title="${title}"` : '';
        return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
      }
    }
  };
}

// Check if we're on auth subdomain at module load time
const isAuthSubdomain = typeof window !== 'undefined' && window.location.hostname.startsWith('auth.');

// Synchronously import social login modules if on auth subdomain
// This ensures providers are available before any component tries to inject them
let SocialLoginModule: any;
let GoogleLoginProvider: any;
let FacebookLoginProvider: any;

if (isAuthSubdomain) {
  // Note: This will be a synchronous import since the module is already in the bundle
  import('@abacritt/angularx-social-login').then(module => {
    SocialLoginModule = module.SocialLoginModule;
    GoogleLoginProvider = module.GoogleLoginProvider;
    FacebookLoginProvider = module.FacebookLoginProvider;
  });
}

// Define providers based on subdomain
function getSocialLoginProviders() {
  if (isAuthSubdomain && SocialLoginModule) {
    try {
      return [
        importProvidersFrom(SocialLoginModule),
        {
          provide: 'SocialAuthServiceConfig',
          useValue: {
            autoLogin: false,
            providers: [
              // We're no longer using GoogleLoginProvider through SocialAuthService
              // This is now handled by Google Identity Services directly
              // We still need the provider for sign-out functionality
              {
                id: GoogleLoginProvider.PROVIDER_ID,
                provider: new GoogleLoginProvider(environment.ggOAuthClientId, {
                  oneTapEnabled: false, // Disable One Tap since we're using the Identity Services API
                  scopes: 'email profile'
                })
              },
              {
                id: FacebookLoginProvider.PROVIDER_ID,
                provider: new FacebookLoginProvider(environment.fbOAuthClientId, {
                  // These settings are critical for subdomain support
                  scope: 'email,public_profile',
                  version: 'v18.0', // Use latest version
                  auth_type: 'rerequest',
                  cookie: true,
                  xfbml: true,
                  // Add this to avoid CORS issues with subdomains
                  fields: 'name,email,picture,first_name,last_name',
                  return_scopes: true
                })
              }
            ],
            onError: (error: any) => {
              console.error('SocialAuthService error:', error);
            }
          }
        }
      ];
    } catch (error) {
      console.error('Failed to configure social login providers:', error);
      return [];
    }
  }

  // Return empty array for non-auth subdomains
  return [];
}

// Create the application config asynchronously to wait for social login module
const createAppConfig = async (): Promise<ApplicationConfig> => {
  // If on auth subdomain, wait for social login module to load
  if (isAuthSubdomain) {
    console.log('Loading social login module for auth subdomain...');
    const socialLoginModule = await import('@abacritt/angularx-social-login');
    SocialLoginModule = socialLoginModule.SocialLoginModule;
    GoogleLoginProvider = socialLoginModule.GoogleLoginProvider;
    FacebookLoginProvider = socialLoginModule.FacebookLoginProvider;
    console.log('Social login module loaded successfully');
  }

  // Get social login providers based on subdomain
  const socialLoginProviders = getSocialLoginProviders();
  console.log('Social login providers configured:', socialLoginProviders.length, 'providers');

  return {
    providers: [
      // Enable zoneless change detection (dual mode - Zone.js still active)
      provideZonelessChangeDetection(),
      provideBrowserGlobalErrorListeners(),
      provideRouter(
        routes,
        withComponentInputBinding(),
        withPreloading(PreloadAllModules),
        withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
        withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
        withNavigationErrorHandler((error) => {
          const errorMessage = error.error?.message ?? '';
          const isMissingChunk =
            errorMessage.includes('Failed to fetch dynamically imported module') ||
            errorMessage.includes('ChunkLoadError');
          if (isMissingChunk) {
            // Reload once to recover from CDN cold-start module map poisoning.
            // sessionStorage prevents infinite reload loops if the chunk is genuinely unavailable.
            const reloadKey = 'chunk-reload-attempted';
            if (!sessionStorage.getItem(reloadKey)) {
              sessionStorage.setItem(reloadKey, '1');
              window.location.reload();
            }
          }
        })
      ),
      provideHttpClient(withXhr(), withInterceptorsFromDi()),
      { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: DevGateInterceptor, multi: true },
      MessageService,
      importProvidersFrom(
        PixelModule.forRoot({ enabled: environment.fbSendEvents, pixelId: environment.fbPixelId }),
        MarkdownModule.forRoot({
          markedExtensions: [{
            provide: MARKED_EXTENSIONS,
            useValue: markedTargetBlankExtension(),
            multi: true
          }]
        })
      ),
      provideNoopAnimations(),
      providePrimeNG({
        ripple: true,
        license: environment.PRIMEUI_LICENSE_KEY,
        theme: {
          preset: MDTThemePreset,
          options: {
            darkModeSelector: false,
            cssLayer: false
          }
        }
      }),
      ModalDialogComponent,
      { provide: WINDOW_TOKEN, useValue: window },
      {
        provide: 'TEAMS_CONFIG',
        useFactory: () => createTeamsConfig({
          ui: { enableMobileOptimizations: true },
          business: { defaultTeamCost: 5 }
        })
      },

      // Spread social login providers (empty array for non-auth subdomains)
      ...socialLoginProviders
    ]
  };
};

// Export a promise that resolves to the app config
export const appConfigPromise = createAppConfig();

// For backward compatibility with existing imports (not used in main.ts anymore)
export const appConfig: ApplicationConfig = {
  providers: [
    // Default providers - should not be used since main.ts waits for appConfigPromise
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideNoopAnimations(),
    MessageService,
    { provide: WINDOW_TOKEN, useValue: window }
  ]
};